import { Anchor, Button, Card, Center, Collapse, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconFilter, IconRefresh, IconSearch } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';
import {
  PaginationBar,
  PIPELINE_STAGES,
  SOURCE_TYPE_LABELS,
  StageBadge,
  formatDate,
  formatMs,
  interpretIngestError,
  stripProtocol,
  ScreenHint,
} from './shared';

// Stage-gradient viz colors, mapped to Mantine palette tokens so they adapt in dark mode.
// `structuring` is the optional AI pass (present only on llm_structured docs), so it isn't
// a job stage — only a timing key — but it must be coloured and counted where it appears.
const STAGE_BAR_COLORS = {
  parsing: 'var(--mantine-color-blue-2)',
  structuring: 'var(--mantine-color-violet-3)',
  enriching: 'var(--mantine-color-blue-3)',
  embedding: 'var(--mantine-color-blue-4)',
  indexing: 'var(--mantine-color-blue-6)',
};
// Display/segment order across every timing key we may record.
const TIMING_ORDER = ['parsing', 'structuring', 'enriching', 'embedding', 'indexing'];
const FAILED_SEGMENT_COLOR = 'var(--mantine-color-red-5)';
const FALLBACK_SEGMENT_MS = 3000;
const IN_PROGRESS_STAGES = ['pending', ...PIPELINE_STAGES];

// Grouped 'in progress' can't be expressed server-side (the API filters one exact
// stage) and client-filtering a server page breaks counts — offer the real stages.
const STAGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All stages' },
  { value: 'failed', label: 'failed' },
  { value: 'ready', label: 'ready' },
  ...IN_PROGRESS_STAGES.map((stage) => ({ value: stage, label: stage })),
];

const timingSegments = (job) => {
  const timings = job.timings || {};
  const segments = TIMING_ORDER.filter((stage) => timings[stage] != null).map((stage) => ({
    stage,
    ms: timings[stage],
    color: STAGE_BAR_COLORS[stage],
  }));
  if (job.stage === 'failed') {
    // The stage it died in has no recorded time — a red tail marks where it stopped.
    segments.push({ stage: 'failed', ms: null, color: FAILED_SEGMENT_COLOR });
  } else if (STAGE_BAR_COLORS[job.stage] && timings[job.stage] == null) {
    segments.push({ stage: job.stage, ms: null, color: STAGE_BAR_COLORS[job.stage], running: true });
  }
  return segments;
};

const totalMs = (job) => Object.values(job.timings || {}).reduce((acc, ms) => acc + (ms || 0), 0);

const durationLabel = (job) => {
  const total = totalMs(job);
  if (job.stage === 'ready') return `total ${formatMs(total)}`;
  if (job.stage === 'failed') return total > 0 ? `${formatMs(total)} → failed` : 'failed';
  return 'running…';
};

const sourceLine = (doc) => {
  const label = SOURCE_TYPE_LABELS[doc.sourceType] || doc.sourceType;
  return doc.sourceUrl ? `${label} · ${stripProtocol(doc.sourceUrl)}` : label;
};

const TimingBar = ({ job }) => {
  const segments = timingSegments(job);
  const total = segments.reduce((acc, segment) => acc + (segment.ms || FALLBACK_SEGMENT_MS), 0) || 1;
  return (
    <div>
      <div
        style={{
          display: 'flex',
          width: 210,
          height: 8,
          borderRadius: 3,
          overflow: 'hidden',
          background: 'var(--mantine-color-gray-1)',
        }}
      >
        {segments.map((segment) => (
          <div
            key={segment.stage}
            style={{
              width: `${Math.max(7, Math.round(((segment.ms || FALLBACK_SEGMENT_MS) / total) * 100))}%`,
              background: segment.color,
              animation: segment.running ? 'kbIngestPulse 1.2s ease-in-out infinite' : undefined,
            }}
          />
        ))}
      </div>
      <Text fz={11} c="dimmed" mt={3}>
        {durationLabel(job)}
      </Text>
    </div>
  );
};

const EmptyState = () => (
  <Center py={56}>
    <Stack align="center" gap={8}>
      <IconSearch size={44} stroke={1} color="var(--mantine-color-gray-5)" />
      <Text fz="lg" fw={500}>
        No results found
      </Text>
      <Text size="sm" c="dimmed">
        No ingest runs match this filter.
      </Text>
    </Stack>
  </Center>
);

const IngestQueueScreen = ({ apiBase, isSuperAdmin, onOpenDocument }) => {
  const fetch = useFetch();
  const [jobs, setJobs] = useState();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [stageFilter, setStageFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [error, setError] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const retryTimer = useRef();

  const fetchJobs = useCallback(() => {
    const stage = stageFilter === 'all' ? undefined : stageFilter;
    fetch(`${apiBase}/jobs?${queryString.stringify({ page, perPage, stage })}`)
      .then((response) => {
        setJobs(response.jobs);
        setTotal(response.total);
        setError(undefined);
      })
      .catch(setError);
  }, [fetch, apiBase, page, perPage, stageFilter]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs, refreshKey]);

  useEffect(() => () => clearTimeout(retryTimer.current), []);

  const retry = (job) => {
    setRetryingId(job.id);
    fetch(`${apiBase}/documents/${job.knowledgeDocument.id}/reingest`, { method: 'POST', body: JSON.stringify({}) })
      .then(() => {
        notifications.show({
          title: 'Re-processing queued',
          message: 'A new pipeline run was queued for this document.',
          color: 'blue',
        });
        setExpandedId((current) => (current === job.id ? null : current));
        // Delayed refetch through state so it uses current filters and dies with the
        // component instead of firing a stale closure after unmount.
        clearTimeout(retryTimer.current);
        retryTimer.current = setTimeout(() => setRefreshKey((key) => key + 1), 1500);
      })
      .catch((err) => {
        notifications.show({ title: 'Retry failed', message: err.message, color: 'danger' });
      })
      .finally(() => {
        setRetryingId((current) => (current === job.id ? null : current));
      });
  };

  // The row shows while it's open or while it's narrowing the list — but then collapsing
  // has to clear the filter too, or the Filters button would look broken (it would toggle
  // state nothing on screen reflects) and the list would stay narrowed for no visible reason.
  const filtersVisible = showFilters || stageFilter !== 'all';

  const toggleFilters = () => {
    if (filtersVisible) {
      setStageFilter('all');
      setPage(1);
      setExpandedId(null);
      setShowFilters(false);
    } else {
      setShowFilters(true);
    }
  };

  if (error) {
    // The stage filter and pager live below this return, so without a control here a
    // failed fetch is a dead end — nothing left on screen can re-trigger it. `useFetch`
    // rejects with the parsed JSON body, which doesn't always carry `.message`.
    return (
      <Group gap="sm" mt="sm">
        <ErrorMessage message={error.message || 'Could not load the ingest queue.'} />
        <Button
          size="xs"
          variant="default"
          leftSection={<IconRefresh size={13} stroke={1.5} />}
          onClick={() => {
            setError(undefined);
            setRefreshKey((key) => key + 1);
          }}
        >
          Try again
        </Button>
      </Group>
    );
  }
  if (jobs === undefined) {
    return <Spinner />;
  }

  return (
    <div>
      <style>{'@keyframes kbIngestPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }'}</style>

      <Group gap={8} align="center" wrap="wrap">
        {filtersVisible && (
          <Select
            w={170}
            aria-label="Processing stage"
            value={stageFilter}
            onChange={(value) => {
              setStageFilter(value || 'all');
              setPage(1);
              setExpandedId(null);
            }}
            data={STAGE_FILTER_OPTIONS}
          />
        )}
        <div style={{ flex: 1 }} />
        <ScreenHint text="Every processing run of your documents, newest first. A failed run never takes content down — the previously indexed version keeps serving until a run succeeds." />
        <Tooltip
          label={stageFilter !== 'all' ? 'Hide and clear the stage filter' : 'Filter the runs by processing stage'}
          withArrow
        >
          <Button variant="default" leftSection={<IconFilter size={15} stroke={1.5} />} onClick={toggleFilters}>
            Filters{stageFilter !== 'all' ? ' · 1' : ''}
          </Button>
        </Tooltip>
      </Group>

      <Card withBorder radius="sm" padding="xs" mt="sm" style={{ overflowX: 'auto' }}>
        {jobs.length === 0 ? (
          <EmptyState />
        ) : (
          jobs.map((job, index) => (
            <div
              key={job.id}
              style={{
                padding: '9px 10px',
                borderBottom: index < jobs.length - 1 ? '1px solid var(--mantine-color-gray-1)' : undefined,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(220px,1.5fr) 100px 230px minmax(190px,1fr) auto',
                  gap: 14,
                  alignItems: 'center',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Group gap={8} wrap="nowrap">
                    {isSuperAdmin && (
                      <Text fz={12} c="gray.5" ff="monospace">
                        #{job.id}
                      </Text>
                    )}
                    <Anchor
                      component="button"
                      fz="sm"
                      fw={600}
                      onClick={() => onOpenDocument(job.knowledgeDocument.id)}
                    >
                      {job.knowledgeDocument.title}
                    </Anchor>
                  </Group>
                  <Text fz={12} c="dimmed" mt={2} truncate>
                    {sourceLine(job.knowledgeDocument)}
                  </Text>
                </div>
                <div>
                  <StageBadge stage={job.stage} error={job.error} />
                </div>
                <TimingBar job={job} />
                <Text fz={12} c="dimmed">
                  {formatDate(job.createdAt)} →{' '}
                  {job.stage === 'ready' || job.stage === 'failed' ? formatDate(job.updatedAt) : '—'}
                </Text>
                <Group gap={6} justify="flex-end" wrap="nowrap">
                  {job.stage === 'failed' && (
                    <Button
                      size="xs"
                      variant="default"
                      leftSection={<IconRefresh size={13} stroke={1.5} />}
                      loading={retryingId === job.id}
                      onClick={() => retry(job)}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    rightSection={<IconChevronDown size={14} stroke={1.5} />}
                    onClick={() => setExpandedId(expandedId === job.id ? null : job.id)}
                  >
                    Details
                  </Button>
                </Group>
              </div>
              <Collapse expanded={expandedId === job.id}>
                <div
                  style={{
                    marginTop: 8,
                    background: 'var(--mantine-color-gray-0)',
                    border: '1px solid var(--mantine-color-gray-2)',
                    borderRadius: 4,
                    padding: '10px 12px',
                  }}
                >
                  <Text fz={11} fw={600} c="dimmed" tt="uppercase" mb={6} style={{ letterSpacing: '0.04em' }}>
                    Processing stages
                  </Text>
                  {TIMING_ORDER.filter((stage) => job.timings?.[stage] != null).length > 0 ? (
                    <Group gap={20} wrap="wrap">
                      {TIMING_ORDER.filter((stage) => job.timings?.[stage] != null).map((stage) => (
                        <div key={stage}>
                          <Group gap={5} align="center" wrap="nowrap">
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: 2,
                                background: STAGE_BAR_COLORS[stage],
                                display: 'inline-block',
                              }}
                            />
                            <Text fz={11} c="dimmed">
                              {stage}
                            </Text>
                          </Group>
                          <Text fz={13} fw={600} mt={1}>
                            {formatMs(job.timings[stage])}
                          </Text>
                        </div>
                      ))}
                    </Group>
                  ) : (
                    <Text fz={12} c="dimmed">
                      No stage completed{job.stage === 'failed' ? ' — it failed before processing started.' : '.'}
                    </Text>
                  )}

                  {job.stage === 'failed' && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--mantine-color-gray-2)' }}>
                      <Text fz={13} fw={600} c="red.9">
                        {interpretIngestError(job.error).title}
                      </Text>
                      <Text fz={12.5} c="dimmed" mt={2}>
                        {interpretIngestError(job.error).hint}
                      </Text>
                      <Text fz={12} c="dimmed" mt={6}>
                        The previously indexed version stays live — the assistant keeps answering from it.
                      </Text>
                      {isSuperAdmin && job.error && (
                        <details style={{ marginTop: 8 }}>
                          <summary style={{ fontSize: 11, color: 'var(--mantine-color-dimmed)', cursor: 'pointer' }}>
                            Technical details <SuperAdminBadge show iconOnly tooltip="Raw error — super admins only" />
                          </summary>
                          <Text fz={11} ff="monospace" c="dimmed" mt={4} style={{ wordBreak: 'break-word' }}>
                            {job.error}
                          </Text>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </Collapse>
            </div>
          ))
        )}
      </Card>

      {total > 0 && (
        <PaginationBar
          shown={jobs.length}
          total={total}
          noun="runs"
          page={page}
          onPageChange={setPage}
          perPage={perPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
          mt="sm"
        />
      )}
    </div>
  );
};

export default IngestQueueScreen;
